/// FOURJS_START_COPYRIGHT(D,2025)
/// Property of Four Js*
/// (c) Copyright Four Js 2025, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

//Required by ESlint not to raise errors
"use strict";

modulum("NavigationButtonService", ["InitService", "MainContainerService"], function (context, cls) {
  /**
   * Service responsible of the Navigation button behavior
   * It create/destroy the button depending on the available actions
   * @namespace gbc.NavigationButtonService
   * @gbcService
   * @publicdoc
   */
  class NavigationButtonService /** @lends gbc.NavigationButtonService */ {
    constructor() {
      /** @type {classes.ItemWidget} */
      this._button = null;
      /** @type {Map<string, Map<string, Object>>} */
      this._actions = new Map();
      /** @type {string} */
      this._currentCategory = null;
      /** @type {string[]} */
      this._categories = ["menu", "back", "apps", "window", "dock", "other"];
      /** @type {string[]} */
      this._hiddenCategories = [];
      /** @type {Function[]} */
      this._mainContainerHandlers = [];
    }

    destroy() {
      for (const unsubscribe of this._mainContainerHandlers) {
        unsubscribe();
      }
      this._mainContainerHandlers.length = 0;

      this._destroyButton();

      this._actions.clear();
      this._hiddenCategories.length = 0;
    }

    init() {
      this._eventListener = new cls.EventListener();
      this._mainContainerHandlers = [
        context.MainContainerService.when(context.constants.widgetEvents.sideBarRailShown, this.hideAppsCategory.bind(this)),
        context.MainContainerService.when(context.constants.widgetEvents.sideBarRailHidden, this.showAppsCategory.bind(this)),
        context.MainContainerService.when(context.constants.widgetEvents.sideBarDrawerDocked, this.hideAppsCategory.bind(this)),
        context.MainContainerService.when(context.constants.widgetEvents.sideBarDrawerUndocked, this.showAppsCategory.bind(this)),
        context.MainContainerService.when(context.constants.widgetEvents.sideBarDrawerShown, () => {
          this._updateChromeBarNavigationZIndex(true);
          this._refreshAriaPopup();
        }),
        context.MainContainerService.when(context.constants.widgetEvents.sideBarDrawerHidden, () => {
          this._updateChromeBarNavigationZIndex(false);
          this._refreshAriaPopup();
        }),
        context.MainContainerService.when(context.constants.widgetEvents.floatingPanelShown, () => this._refreshAriaPopup()),
        context.MainContainerService.when(context.constants.widgetEvents.floatingPanelHidden, () => this._refreshAriaPopup())
      ];
    }

    /**
     * Create the navigation button and add it to the DOM
     */
    _createButton() {
      if (this._button) {
        return;
      }
      this._button = cls.WidgetFactory.createWidget("Item", {
        appHash: gbc.systemAppId
      });
      this._button.addClasses("navigationButton", context.constants.widgetCssClasses.chromeItem);
      this._button.setQAInfo("name", "gbc_chromeBarNavigationButton");
      context.MainContainerService.setChromeBarStartContent(this._button.getElement());
      // on click we loop on current category actions to execute
      this._onClickHandler = this._button.onClick(this._executeClickAction.bind(this));
    }

    /**
     * Remove the Navigation button from the dom, then destroy it
     */
    _destroyButton() {
      if (!this._button) {
        return;
      }
      this._onClickHandler();
      this._onClickHandler = null;
      context.MainContainerService.setChromeBarStartContent();
      this._button.destroy();
      this._button = null;
    }

    /**
     * Add an action to the list and update the button aspect and behavior
     * @param {string} category - category to which belongs the action. Icon is built using the category name.
     * @param {string} id - id used to store the action
     * @param {?Function} clickAction - click action to execute on sidebar button click (optional)
     */
    addAction(category, id, clickAction) {
      if (!context.MainContainerService.getChromeBar()) {
        return;
      }

      if (!this._actions.has(category)) {
        this._actions.set(category, new Map());
      }
      const categoryActions = this._actions.get(category);
      if (categoryActions.has(id)) {
        // Stop here if the action already exists
        return;
      }
      categoryActions.set(id, clickAction);
      // 2. Update current sidebar button depending on priority
      this.updateButton();
    }

    /**
     * Remove the associated action from the list
     * and update the button aspect and behavior
     * @param {string} category
     * @param {string} id
     */
    removeAction(category, id) {
      if (!context.MainContainerService.getChromeBar() || !this._actions.has(category)) {
        return;
      }

      const categoryAction = this._actions.get(category);
      categoryAction.delete(id);
      if (categoryAction.size === 0) {
        this._actions.delete(category);
        this.updateButton();
      }
    }

    /**
     * Hide the 'apps' category from being used in navigation button
     */
    hideAppsCategory() {
      this.hideCategory("apps");
    }

    /**
     * Show the 'apps' category back in the navigation button, if it was hidden.
     */
    showAppsCategory() {
      // if sidebardrawer is docked, don't show app icon
      if (context.MainContainerService.isSideBarDrawerDocked() || context.MainContainerService.isSideBarDrawerEmpty()) {
        return;
      }
      this.showCategory("apps");
    }

    /**
     * Add category passed as parameter in hidden categories.
     * @param categoryName
     */
    hideCategory(categoryName) {
      if (!this._hiddenCategories.includes(categoryName)) {
        this._hiddenCategories.push(categoryName);
        this.updateButton();
      }
    }

    /**
     * Remove category passed as parameter from hidden categories.
     * @param categoryName
     */
    showCategory(categoryName) {
      const index = this._hiddenCategories.indexOf(categoryName);
      if (index !== -1) {
        this._hiddenCategories.splice(index, 1);
        this.updateButton();
      }
    }

    _updateChromeBarNavigationZIndex(isDrawerVisible) {
      if (!this._button) {
        return;
      }
      if (context.MainContainerService.isSideBarDrawerDocked()) {
        return;
      }
      this._button.toggleClass("gbc_ChromeBarNavigationButton--overlayContext", isDrawerVisible);
    }

    /**
     * Update current chromebar navigation button
     * being displayed by checking which is
     * the current highest priority category of actions to display
     */
    updateButton() {
      const hasVisibleActions = this._categories.some((cat) => this._actions.has(cat) && !this._hiddenCategories.includes(cat));

      if (!hasVisibleActions) {
        this._currentCategory = null;
        this._destroyButton();
        return;
      }

      // get chromebar button action with highest priority (lowest number)
      let currentCategory = null;
      for (const cat of this._categories) {
        if (this._actions.has(cat) && !this._hiddenCategories.includes(cat)) {
          currentCategory = cat;
          break;
        }
      }

      // update button
      if (this._currentCategory && this._currentCategory === currentCategory) {
        return;
      }
      // execute display action(s) on old category
      this._currentCategory = currentCategory;

      if (!this._button) {
        // create sidebar button if we didn't yet
        this._createButton();
      }
      // update current chromebar navigation button icon
      this._button.setPrimaryIcon(this._currentCategory);
      this._refreshAriaPopup();
    }

    /**
     * Refresh aria-haspopup, aria-controls and aria-expanded on the navigation button to reflect
     * the popup currently associated with the active category. Categories that do not open a
     * popup (e.g. "back") clear the attributes entirely.
     * @private
     */
    _refreshAriaPopup() {
      if (!this._button) {
        return;
      }
      let controlledId = null;
      let expanded = false;
      // When the drawer is docked, it's persistent navigation (not a popup) — the button does not
      // open anything, so the disclosure attributes don't apply for the "apps" category.
      const drawerIsPopup = !context.MainContainerService.isSideBarDrawerDocked();
      switch (this._currentCategory) {
        case "apps":
          if (drawerIsPopup) {
            controlledId = context.MainContainerService.getSideBarDrawer()?.id;
            expanded = context.MainContainerService.isSideBarDrawerVisible();
          }
          break;
        case "menu":
        case "window":
          controlledId = context.MainContainerService.getFloatingPanel()?.id;
          expanded = context.MainContainerService.isFloatingPanelVisible();
          break;
      }
      if (controlledId) {
        // Both possible targets (SideBarDrawer <nav> and FloatingPanel <div>) carry role="dialog" while in
        // popup mode — see MainContainerWidget.tpl.html and dock/undock logic in
        // MainContainerService. aria-haspopup must match the controlled element's role.
        this._button.setAriaAttribute("haspopup", "dialog");
        this._button.setAriaAttribute("controls", controlledId);
        this._button.setAriaAttribute("expanded", expanded);
      } else {
        this._button.setAriaAttribute("haspopup", null);
        this._button.setAriaAttribute("controls", null);
        this._button.setAriaAttribute("expanded", null);
      }
    }

    /**
     * Execute current click action(s) bound to chromebar navigation button
     * @private
     */
    _executeClickAction() {
      const actionsToExecute = this._actions.get(this._currentCategory);
      if (!actionsToExecute) {
        return;
      }
      for (let action of actionsToExecute.values()) {
        action();
      }
    }
  }

  gbc.NavigationButtonService = new NavigationButtonService();
  context.InitService.register(context.NavigationButtonService);
});
